import * as React from 'react';
import { FC, memo } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import Picture from 'components/picture';
import { LoyaltyReward } from 'state/loyalty/types';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';

import {
  AddIconButton,
  AlertIcon,
  BackgroundImage,
  CardContainer,
  ImageWrapper,
  InfoContainer,
  InlineAlertContainer,
  InlineAlertSpacer,
  InlineAlertText,
  RewardText,
  StyledClickableContainer,
  StyledRewardLock,
} from './incentive-card.styled';
import { parseIncentiveData } from './parse-incentive-data';

export interface IIncentiveCard {
  incentive: LoyaltyReward;
  handlePress: ILoyaltyRewardsViewProps['handleOnPress'];
  locked?: boolean;
  inlineAlertMessage?: React.ReactNode;
  isPictureBlurhashEnabled?: boolean;
}

export const IncentiveCard: FC<React.PropsWithChildren<IIncentiveCard>> = memo(
  ({
    incentive,
    handlePress,
    locked = false,
    inlineAlertMessage,
    isPictureBlurhashEnabled = true,
  }) => {
    const incentiveData = parseIncentiveData(incentive);

    const { image, imageDescription, name, backgroundImage } = incentiveData;

    const { formatMessage } = useIntl();

    return (
      <CardContainer>
        <StyledClickableContainer
          isLocked={locked || !!inlineAlertMessage}
          onPress={() => (!locked ? handlePress(incentive) : null)}
        >
          <InfoContainer>
            {image ? (
              <ImageWrapper>
                {backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
                <Picture
                  isBlurhashEnabled={isPictureBlurhashEnabled}
                  image={image}
                  alt={imageDescription}
                  {...incentiveCardImageDefaultProps}
                />
              </ImageWrapper>
            ) : null}
            <RewardText>{name}</RewardText>
          </InfoContainer>
          {locked ? (
            <StyledRewardLock accessibilityLabel={formatMessage({ id: 'locked' })} />
          ) : (
            <>
              {inlineAlertMessage ? (
                <>
                  <InlineAlertSpacer></InlineAlertSpacer>
                  <InlineAlertContainer>
                    <AlertIcon />
                    <InlineAlertText>{inlineAlertMessage}</InlineAlertText>
                  </InlineAlertContainer>
                </>
              ) : (
                <AddIconButton
                  variant="outline"
                  onPress={() => handlePress(incentive)}
                  icon={<Icon variant="add" />}
                  disabled={locked}
                  testID="add-to-cart"
                  accessibilityLabel={formatMessage({ id: 'addToCart' })}
                />
              )}
            </>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
